import { GetServerSideProps } from 'next';
import { groq, PortableText } from 'next-sanity';
import { ArticleJsonLd, NextSeo } from 'next-seo';
import { PageContainer, PageSectionLarge } from '@/components/base/Sections';
import { NotFound } from '@/components/base/States';
import { TypographyText, TypographyTitle } from '@/components/base/Typography';
import { portableTextComponents } from '@/components/sanity/PortableTextComponents';
import { sanityClient } from '@/config/sanity';

const Page = ({ articleData }: any) => {
  const seoBase = {
    title: `Dream Diamonds | ${articleData.title}`,
    description: `Article by ${articleData?.author?.name || 'Dream Diamonds'}`,
  };
  if (!articleData) return <NotFound className="" textClassName="" />;

  return (
    <>
      <NextSeo {...seoBase} />
      <ArticleJsonLd
        authorName={articleData?.author?.name}
        datePublished={articleData?.publishedAt}
        description={articleData?.title}
        images={[articleData?.mainImage?.asset?.url]}
        publisherName="Dream Diamonds"
        title={articleData?.title}
        type="BlogPosting"
        url={`${process.env.NEXT_PUBLIC_CLIENT_URL}/education/${articleData?.slug}`}
      />
      <PageContainer className="">
        <PageSectionLarge className="space-y-8">
          <div className="space-y-4 text-center">
            <TypographyTitle className="text-center">{articleData?.title}</TypographyTitle>

            <TypographyText className="text-muted-foreground block text-center">
              Published on{' '}
              {new Date(articleData?.publishedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </TypographyText>
            <TypographyText className="text-muted-foreground block text-center">
              By {articleData?.author?.name || 'Unknown Author'}
            </TypographyText>

            {articleData.mainImage?.asset?.url && (
              <img
                src={articleData.mainImage.asset.url}
                alt={articleData.title}
                className="mx-auto mt-4 h-64 rounded-md object-cover"
              />
            )}
          </div>
          <div className="space-y-8">
            <div className="prose prose-lg max-w-none">
              {articleData.body && (
                <PortableText value={articleData.body} components={portableTextComponents} />
              )}
            </div>
          </div>
          {articleData?.categories && articleData.categories.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {articleData.categories.map((category: any, idx: number) => (
                <span
                  key={idx}
                  className="bg-primary/10 text-primary rounded-full px-3 py-1 text-sm"
                >
                  {category.title}
                </span>
              ))}
            </div>
          )}
        </PageSectionLarge>
      </PageContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const { article: rawSlug } = params || {};
  const slug = Array.isArray(rawSlug) ? rawSlug[0] : rawSlug;
  if (!slug) return { notFound: true };

  const query = groq`
    *[_type == "post" && slug.current == $slug][0]{
      _id,
      title,
      "slug": slug.current,
      body[]{
        ...,
        _type == "image" => {
          ...,
          asset->{
            _id,
            _ref,
            url
          }
        }
      },
      mainImage {
        asset->{
          _id,
          _ref,
          url
        }
      },
      author->{ name },
      categories[]->{title},
      publishedAt
    }
  `;

  try {
    const articleData = await sanityClient.fetch(query, { slug });
    if (!articleData) return { notFound: true };

    return { props: { articleData } };
  } catch (error) {
    console.error('Error fetching article:', error);
    return { notFound: true };
  }
};

export default Page;
